import { useAppSelector } from '@/app/redux/hooks';
import { selectShareLink } from '@/app/redux/slices/shareLinkSlice';
import InitialsCircle from '@/common/ui/InitialsCircle';
import { DataLoader } from '@/common/ui/LoadingSpinners';
import { formatTimeTaken } from '@/common/utils/datetime';
import { ExportIcon, LinkIcon } from '@phosphor-icons/react';
import { Button } from '@shadcn/components/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@shadcn/components/dialog';
import { Separator } from '@shadcn/components/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@shadcn/components/tooltip';
import dayjs from 'dayjs';
import React, { ReactNode } from 'react';
import styled from 'styled-components';

interface GenericResultDialogueProps {
  open: boolean;
  onClose: () => void;
  title: string;
  studentName: string;
  description: string;
  isLoading: boolean;
  startedAt: number;
  endedAt: number | null;
  completed: boolean;
  controlTypeIcons?: ReactNode;
  correctAttempts: number;
  totalAttempts: number;
  placementLevel?: number;
  onExport: (() => void) | undefined;
  onShare: (() => void) | undefined;
  renderAttemptsTable: () => React.ReactNode;
}
export default function GenericResultDialogue({
  open,
  onClose,
  title,
  studentName,
  description,
  isLoading,
  startedAt,
  endedAt,
  completed,
  controlTypeIcons,
  correctAttempts,
  totalAttempts,
  placementLevel,
  onExport,
  onShare,
  renderAttemptsTable,
}: GenericResultDialogueProps) {
  const { shareLink } = useAppSelector(selectShareLink);

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="w-xl max-w-3xl"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="flex items-end justify-between mr-6">
          <DialogHeader aria-describedby="description">
            <DialogTitle className="flex items-baseline gap-2">
              <span>{title}</span>
              <span className="text-sm text-muted-foreground">
                {dayjs(startedAt).format('DD/MM/YYYY h:mm a')}
              </span>
            </DialogTitle>
            <div
              aria-roledescription="description"
              className="flex items-center gap-2 font-normal text-sm text-muted-foreground"
            >
              <span className="flex items-center gap-2">
                <InitialsCircle userName={studentName} size={20} />
                <span>{studentName}</span>
              </span>
              <Separator orientation="vertical" className="h-4" />
              <span>{description}</span>
            </div>
          </DialogHeader>

          <div className="flex items-center gap-2">
            {onExport && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    className="text-muted-foreground"
                    size="icon"
                    onClick={onExport}
                  >
                    <ExportIcon size={20} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Download results as CSV</TooltipContent>
              </Tooltip>
            )}

            {onShare && !shareLink && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    className="text-muted-foreground"
                    size="icon"
                    onClick={onShare}
                  >
                    <LinkIcon size={16} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Share activity</TooltipContent>
              </Tooltip>
            )}
          </div>
        </div>

        {isLoading ? (
          <div className="h-full w-full grid place-items-center">
            <DataLoader />
          </div>
        ) : (
          <DialogContentContainer>
            <ActivityDetails>
              <DetailItem>
                {endedAt && (
                  <>
                    <span>Time Taken:</span>
                    {formatTimeTaken(startedAt, endedAt)}
                  </>
                )}
                {!completed && (
                  <span className="font-semibold">(Not Completed)</span>
                )}
              </DetailItem>
              {placementLevel && (
                <DetailItem>Placement: Level {placementLevel}</DetailItem>
              )}
              <DetailItem>
                {correctAttempts}/{totalAttempts} Correct
              </DetailItem>
              <DetailItem>
                {totalAttempts > 0
                  ? Math.round((correctAttempts / totalAttempts) * 100)
                  : 0}
                % Accuracy
              </DetailItem>
              {controlTypeIcons && (
                <DetailItem>Control(s) used:{controlTypeIcons}</DetailItem>
              )}
            </ActivityDetails>
            <AttemptsTableContainer>
              {renderAttemptsTable()}
            </AttemptsTableContainer>
          </DialogContentContainer>
        )}
      </DialogContent>
    </Dialog>
  );
}

const DialogContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ActivityDetails = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const DetailItem = styled.div`
  font-size: 14px;
  color: hsl(var(--muted-foreground));
  display: flex;
  align-items: center;
  gap: 0.25em;
`;

const AttemptsTableContainer = styled.div`
  overflow-y: auto;
  max-height: 500px;
`;
